export interface Camera {
  /**
   * Absolute world scale: `1` draws every cell at exactly `CELL_SIZE` pixels, whatever the grid or
   * the viewport happens to measure. Deliberately *not* a multiple of a fit-to-viewport scale — a
   * grid large enough to need panning has no meaningful "the whole thing fits" unit to zoom against,
   * and pinning `1` to it made the same zoom mean a different cell size on every map size.
   */
  zoom: number;
  panX: number;
  panY: number;
}

/** A camera read against the grid it is looking at — the pair the HUD needs to describe the view. */
export interface CameraView {
  camera: Camera;
  /** The zoom that shows the whole grid, which is also the zoom floor. */
  fitZoom: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Cells stop being readable well before this; past it, panning is the better answer than zooming. */
export const MAX_ZOOM = 2.4;
export const ZOOM_STEP = 1.25;

/** Breathing room kept between the fitted grid and the viewport edge, in screen pixels. */
const FIT_MARGIN = 24;

/** Guards float comparisons against a zoom that was arrived at by repeated multiplication. */
const ZOOM_EPSILON = 1e-6;

export const DEFAULT_CAMERA_VIEW: CameraView = {
  camera: { zoom: 1, panX: 0, panY: 0 },
  fitZoom: 1,
};

/**
 * The zoom at which `grid` (in world pixels) sits entirely inside `screen`. Capped at
 * {@link MAX_ZOOM} so a small map fills the viewport without inflating to absurd cells, but given no
 * floor — a floor is exactly what stopped "fit" from actually fitting once the map outgrew it.
 */
export function fitZoom(grid: Size, screen: Size): number {
  if (grid.width <= 0 || grid.height <= 0) return 1;
  const usableWidth = Math.max(1, screen.width - FIT_MARGIN);
  const usableHeight = Math.max(1, screen.height - FIT_MARGIN);
  return Math.min(MAX_ZOOM, usableWidth / grid.width, usableHeight / grid.height);
}

export function fitCamera(grid: Size, screen: Size): Camera {
  return { zoom: fitZoom(grid, screen), panX: 0, panY: 0 };
}

export function clampZoom(zoom: number, grid: Size, screen: Size): number {
  return Math.min(MAX_ZOOM, Math.max(fitZoom(grid, screen), zoom));
}

/** Screen-space top-left of the grid: centred at its scaled size, then offset by the pan. */
export function cameraOrigin(camera: Camera, grid: Size, screen: Size): Point {
  return {
    x: (screen.width - grid.width * camera.zoom) / 2 + camera.panX,
    y: (screen.height - grid.height * camera.zoom) / 2 + camera.panY,
  };
}

/**
 * Bounds the camera to what a player can actually make sense of: zoom between the fit and the stop,
 * and pan only ever across ground that is off-screen. An axis whose content already fits snaps back
 * to centred, which is why a fitted view has nothing to drag and only a zoomed-in one does.
 */
export function clampCamera(camera: Camera, grid: Size, screen: Size): Camera {
  const zoom = clampZoom(camera.zoom, grid, screen);
  return {
    zoom,
    panX: clampPanAxis(camera.panX, grid.width * zoom, screen.width),
    panY: clampPanAxis(camera.panY, grid.height * zoom, screen.height),
  };
}

function clampPanAxis(pan: number, scaled: number, screen: number): number {
  if (scaled <= screen) return 0;
  const limit = (scaled - screen) / 2;
  return Math.min(limit, Math.max(-limit, pan));
}

/** Zooms about a fixed screen point, so whatever sits under the cursor stays under the cursor. */
export function zoomCameraAt(
  camera: Camera,
  factor: number,
  anchor: Point,
  grid: Size,
  screen: Size,
): Camera {
  const zoom = clampZoom(camera.zoom * factor, grid, screen);
  if (zoom === camera.zoom) return camera;
  const origin = cameraOrigin(camera, grid, screen);
  const ratio = zoom / camera.zoom;
  const centred = cameraOrigin({ zoom, panX: 0, panY: 0 }, grid, screen);
  return clampCamera(
    {
      zoom,
      panX: anchor.x - (anchor.x - origin.x) * ratio - centred.x,
      panY: anchor.y - (anchor.y - origin.y) * ratio - centred.y,
    },
    grid,
    screen,
  );
}

/** The button/keyboard form of {@link zoomCameraAt}, anchored on the middle of the view. */
export function zoomCameraBy(camera: Camera, factor: number, grid: Size, screen: Size): Camera {
  const centre = { x: screen.width / 2, y: screen.height / 2 };
  return zoomCameraAt(camera, factor, centre, grid, screen);
}

export function panCamera(
  camera: Camera,
  dx: number,
  dy: number,
  grid: Size,
  screen: Size,
): Camera {
  return clampCamera({ ...camera, panX: camera.panX + dx, panY: camera.panY + dy }, grid, screen);
}

export function isFitted(view: CameraView): boolean {
  return (
    view.camera.zoom <= view.fitZoom + ZOOM_EPSILON &&
    view.camera.panX === 0 &&
    view.camera.panY === 0
  );
}

export function canZoomIn(view: CameraView): boolean {
  return view.camera.zoom < MAX_ZOOM - ZOOM_EPSILON;
}

export function canZoomOut(view: CameraView): boolean {
  return view.camera.zoom > view.fitZoom + ZOOM_EPSILON;
}
